import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import apiClient from '../services/api';
import { websocketService } from '../services/websocket';
import { notify } from '../utils/notify';
import './Escalations.css';

function Escalations() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [stats, setStats] = useState(null);
  const [escalations, setEscalations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEscalation, setSelectedEscalation] = useState(null);
  const [filters, setFilters] = useState({
    status: null,
    escalation_type: null
  });
  const [pagination, setPagination] = useState({
    limit: 20,
    offset: 0,
    total: 0
  });
  const [resolveModal, setResolveModal] = useState({ open: false, escalation: null });
  const [assignModal, setAssignModal] = useState({ open: false, escalation: null, assignedTo: '' });
  const [resolveNotes, setResolveNotes] = useState('');
  const [notification, setNotification] = useState(null);
  const notificationTimeoutRef = useRef(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/v1/escalations/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch escalation stats:', error);
    }
  }, []);

  const fetchEscalations = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        offset: pagination.offset.toString()
      });
      if (filters.status) params.append('status', filters.status);
      if (filters.escalation_type) params.append('escalation_type', filters.escalation_type);

      const res = await apiClient.get(`/api/v1/escalations/?${params.toString()}`);
      setEscalations(res.data.escalations || []);
      setPagination(prev => ({
        ...prev,
        total: res.data.total || 0
      }));
    } catch (error) {
      console.error('Failed to fetch escalations:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit, pagination.offset]);

  // WebSocket connection for real-time notifications
  useEffect(() => {
    if (!user || !user.id) {
      return;
    }

    let isMounted = true;

    // Check if token exists
    const token = localStorage.getItem('token');
    if (!token) {
      // No token - just fetch initial data, no WebSocket
      fetchStats();
      fetchEscalations();
      return;
    }

    const handleWebSocketMessage = (message) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('Escalations: WebSocket message received:', message);
      }
      
      if (!isMounted) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Escalations: Component not mounted, ignoring message');
        }
        return;
      }
      
      if (message.type === 'escalation_notification') {
        const escalationData = message.data;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Escalations: Processing escalation notification:', escalationData);
        }
        
        // Validate and sanitize escalation data
        if (!escalationData || typeof escalationData !== 'object') {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Escalations: Invalid escalation data received');
          }
          return;
        }
        
        // Validate required fields and sanitize
        const escalationId = parseInt(escalationData.escalation_id, 10);
        if (isNaN(escalationId) || escalationId <= 0) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Escalations: Invalid escalation_id');
          }
          return;
        }
        
        const contactName = String(escalationData.contact_name || '').trim().substring(0, 255);
        if (!contactName) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Escalations: Missing contact_name');
          }
          return;
        }
        
        const notificationType = ['new_escalation', 'sla_breach', 'sla_warning'].includes(escalationData.type)
          ? escalationData.type
          : 'new_escalation';
        
        // Show notification
        setNotification({
          type: notificationType,
          escalationId: escalationId,
          contactName: contactName,
          message: notificationType === 'sla_breach' 
            ? t('escalations.notifications.sla_breach', { name: contactName, hours: escalationData.hours_overdue || 0 })
            : notificationType === 'sla_warning'
            ? t('escalations.notifications.sla_warning', { name: contactName, hours: escalationData.hours_remaining || 0 })
            : t('escalations.notifications.new_escalation', { name: contactName })
        });

        // Auto-hide notification after 5 seconds
        if (notificationTimeoutRef.current) {
          clearTimeout(notificationTimeoutRef.current);
        }
        notificationTimeoutRef.current = setTimeout(() => {
          if (isMounted) {
            setNotification(null);
          }
        }, 5000);

        // Refresh data on real-time notification
        fetchStats();
        fetchEscalations();
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('Escalations: Ignoring message with type:', message.type);
        }
      }
    };

    const handleWebSocketError = (error) => {
      if (isMounted && process.env.NODE_ENV === 'development') {
        console.warn('WebSocket error:', error?.message || 'Connection failed');
      }
      // WebSocket failed - user can manually refresh if needed
    };

    // Fetch initial data
    fetchStats();
    fetchEscalations();

    // Connect to WebSocket for real-time notifications
    // Use a small delay to ensure component is fully mounted
    const connectTimeout = setTimeout(() => {
      try {
        websocketService.connect(user.id, handleWebSocketMessage, handleWebSocketError);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to initialize WebSocket:', error);
        }
      }
    }, 100);

    // Cleanup timeout on unmount
    return () => {
      clearTimeout(connectTimeout);
      isMounted = false;
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
      websocketService.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Only depend on user.id, not the functions

  // Manual refresh handler (fallback if WebSocket fails)
  const handleManualRefresh = () => {
    fetchStats();
    fetchEscalations();
  };

  // Fetch escalations when filters, pagination, or user changes
  useEffect(() => {
    if (user?.id) {
      fetchEscalations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.escalation_type, pagination.limit, pagination.offset, user?.id]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handleViewDetails = async (escalationId) => {
    try {
      const res = await apiClient.get(`/api/v1/escalations/${escalationId}`);
      setSelectedEscalation(res.data);
    } catch (error) {
      console.error('Failed to fetch escalation details:', error);
      notify.error(t('escalations.error.fetch_details'));
    }
  };

  const handleResolve = async () => {
    if (!resolveModal.escalation || !resolveNotes.trim()) {
      notify.warning(t('escalations.error.notes_required'));
      return;
    }

    try {
      await apiClient.post(`/api/v1/escalations/${resolveModal.escalation.id}/resolve`, {
        resolution_notes: resolveNotes,
        assigned_to: resolveModal.escalation.assigned_to || null
      });
      setResolveModal({ open: false, escalation: null });
      setResolveNotes('');
      fetchEscalations();
      fetchStats();
      if (selectedEscalation?.id === resolveModal.escalation.id) {
        handleViewDetails(resolveModal.escalation.id);
      }
    } catch (error) {
      console.error('Failed to resolve escalation:', error);
      notify.error(t('escalations.error.resolve_failed'));
    }
  };

  const handleAssign = async () => {
    if (!assignModal.escalation || !assignModal.assignedTo.trim()) {
      notify.warning(t('escalations.error.assignee_required'));
      return;
    }

    try {
      await apiClient.post(`/api/v1/escalations/${assignModal.escalation.id}/assign?assigned_to=${encodeURIComponent(assignModal.assignedTo)}`);
      setAssignModal({ open: false, escalation: null, assignedTo: '' });
      fetchEscalations();
      if (selectedEscalation?.id === assignModal.escalation.id) {
        handleViewDetails(assignModal.escalation.id);
      }
    } catch (error) {
      console.error('Failed to assign escalation:', error);
      notify.error(t('escalations.error.assign_failed'));
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'pending': 'status-pending',
      'collecting_data': 'status-collecting',
      'awaiting_review': 'status-awaiting',
      'in_progress': 'status-progress',
      'resolved': 'status-resolved',
      'cancelled': 'status-cancelled'
    };
    return statusMap[status] || 'status-default';
  };

  const getPriorityBadgeClass = (priority) => {
    const priorityMap = {
      'urgent': 'priority-urgent',
      'high': 'priority-high',
      'normal': 'priority-normal',
      'low': 'priority-low'
    };
    return priorityMap[priority] || 'priority-normal';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString(t('common.locale') || 'it-IT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('escalations.time.just_now');
    if (diffMins < 60) return t('escalations.time.minutes_ago', { count: diffMins });
    if (diffHours < 24) return t('escalations.time.hours_ago', { count: diffHours });
    return t('escalations.time.days_ago', { count: diffDays });
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return null;
    // Add + prefix if not already present
    return phone.startsWith('+') ? phone : `+${phone}`;
  };

  return (
    <div className="container escalations-page">
      <div className="title-with-back">
        <button className="back-button" onClick={() => navigate('/dashboard')} title={t('common.back_to_dashboard')}>
          <i className="bi bi-arrow-left"></i>
        </button>
        <h1>{t('escalations.title')}</h1>
        <div className="header-actions">
          {websocketService.isConnected() ? (
            <span className="websocket-indicator connected" title={t('escalations.realtime_connected')}>
              <i className="bi bi-wifi"></i>
              <span>{t('escalations.realtime_connected')}</span>
            </span>
          ) : (
            <span className="websocket-indicator disconnected" title={t('escalations.realtime_disconnected')}>
              <i className="bi bi-wifi-off"></i>
              <span>{t('escalations.realtime_disconnected')}</span>
            </span>
          )}
          <button 
            className="refresh-button" 
            onClick={handleManualRefresh}
            title={t('escalations.refresh')}
            disabled={loading}
          >
            <i className={`bi bi-arrow-clockwise ${loading ? 'spinning' : ''}`}></i>
            {t('escalations.refresh')}
          </button>
        </div>
      </div>

      {/* Real-time Notification Toast */}
      {notification && (
        <div 
          className={`notification-toast ${notification.type}`}
          onClick={() => {
            if (notification.escalationId) {
              handleViewDetails(notification.escalationId);
            }
            setNotification(null);
          }}
        >
          <div className="notification-content">
            <i className={`bi ${notification.type === 'sla_breach' ? 'bi-exclamation-triangle-fill' : notification.type === 'sla_warning' ? 'bi-clock-fill' : 'bi-bell-fill'}`}></i>
            <span>{notification.message}</span>
          </div>
          <button className="notification-close" onClick={(e) => { e.stopPropagation(); setNotification(null); }}>
            <i className="bi bi-x"></i>
          </button>
        </div>
      )}

      {/* Stats Dashboard */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon pending">
              <i className="bi bi-clock-history"></i>
            </div>
            <div className="stat-content">
              <h3>{t('escalations.stats.pending')}</h3>
              <div className="stat-value">{stats.pending || 0}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon resolved">
              <i className="bi bi-check-circle"></i>
            </div>
            <div className="stat-content">
              <h3>{t('escalations.stats.resolved_today')}</h3>
              <div className="stat-value">{stats.resolved_today || 0}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon week">
              <i className="bi bi-calendar-week"></i>
            </div>
            <div className="stat-content">
              <h3>{t('escalations.stats.total_this_week')}</h3>
              <div className="stat-value">{stats.total_this_week || 0}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon sla">
              <i className="bi bi-exclamation-triangle"></i>
            </div>
            <div className="stat-content">
              <h3>{t('escalations.stats.sla_breached')}</h3>
              <div className="stat-value warning">{stats.sla_breached || 0}</div>
            </div>
          </div>
          {stats.average_resolution_time_hours !== undefined && (
            <div className="stat-card">
              <div className="stat-icon time">
                <i className="bi bi-hourglass-split"></i>
              </div>
              <div className="stat-content">
                <h3>{t('escalations.stats.avg_resolution_time')}</h3>
                <div className="stat-value">{stats.average_resolution_time_hours.toFixed(1)}h</div>
              </div>
            </div>
          )}
          {stats.sla_compliance_rate !== null && stats.sla_compliance_rate !== undefined && (
            <div className="stat-card">
              <div className="stat-icon compliance">
                <i className="bi bi-graph-up"></i>
              </div>
              <div className="stat-content">
                <h3>{t('escalations.stats.sla_compliance')}</h3>
                <div className="stat-value">{stats.sla_compliance_rate.toFixed(1)}%</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>{t('escalations.filters.status')}</label>
          <select
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value || null)}
          >
            <option value="">{t('escalations.filters.all_statuses')}</option>
            <option value="pending">{t('escalations.status.pending')}</option>
            <option value="collecting_data">{t('escalations.status.collecting_data')}</option>
            <option value="awaiting_review">{t('escalations.status.awaiting_review')}</option>
            <option value="in_progress">{t('escalations.status.in_progress')}</option>
            <option value="resolved">{t('escalations.status.resolved')}</option>
            <option value="cancelled">{t('escalations.status.cancelled')}</option>
          </select>
        </div>
        <div className="filter-group">
          <label>{t('escalations.filters.type')}</label>
          <select
            value={filters.escalation_type || ''}
            onChange={(e) => handleFilterChange('escalation_type', e.target.value || null)}
          >
            <option value="">{t('escalations.filters.all_types')}</option>
            <option value="purchase_intent">{t('escalations.type.purchase_intent')}</option>
            <option value="negative_sentiment">{t('escalations.type.negative_sentiment')}</option>
            <option value="explicit_request">{t('escalations.type.explicit_request')}</option>
            <option value="knowledge_gap">{t('escalations.type.knowledge_gap')}</option>
            <option value="custom">{t('escalations.type.custom')}</option>
          </select>
        </div>
      </div>

      {/* Escalations List */}
      <div className="escalations-list-section">
        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : escalations.length === 0 ? (
          <div className="empty-state">
            <i className="bi bi-inbox"></i>
            <p>{t('escalations.empty_state')}</p>
          </div>
        ) : (
          <>
            <div className="escalations-table">
              {escalations.map((escalation) => (
                <div key={escalation.id} className="escalation-card">
                  <div className="escalation-header">
                    <div className="escalation-id">
                      <span className="badge">#{escalation.id}</span>
                      <span className={`status-badge ${getStatusBadgeClass(escalation.status)}`}>
                        {t(`escalations.status.${escalation.status}`)}
                      </span>
                      <span className={`priority-badge ${getPriorityBadgeClass(escalation.priority)}`}>
                        {t(`escalations.priority.${escalation.priority}`)}
                      </span>
                    </div>
                    <div className="escalation-actions">
                      <button
                        className="btn-view"
                        onClick={() => handleViewDetails(escalation.id)}
                      >
                        <i className="bi bi-eye"></i> {t('escalations.actions.view')}
                      </button>
                      {escalation.status !== 'resolved' && escalation.status !== 'cancelled' && (
                        <>
                          <button
                            className="btn-assign"
                            onClick={() => setAssignModal({ open: true, escalation, assignedTo: escalation.assigned_to || '' })}
                          >
                            <i className="bi bi-person-plus"></i> {t('escalations.actions.assign')}
                          </button>
                          <button
                            className="btn-resolve"
                            onClick={() => setResolveModal({ open: true, escalation })}
                          >
                            <i className="bi bi-check"></i> {t('escalations.actions.resolve')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="escalation-body">
                    <div className="escalation-info">
                      <div className="info-row">
                        <span className="info-label">{t('escalations.contact.name')}:</span>
                        <span className="info-value">{escalation.contact_name || '-'}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">{t('escalations.contact.email')}:</span>
                        <span className="info-value">
                          {escalation.contact_email ? (
                            <a href={`mailto:${escalation.contact_email}`}>{escalation.contact_email}</a>
                          ) : '-'}
                        </span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">{t('escalations.contact.phone')}:</span>
                        <span className="info-value">
                          {escalation.contact_phone ? (
                            (() => {
                              const formattedPhone = formatPhoneNumber(escalation.contact_phone);
                              return <a href={`tel:${formattedPhone}`}>{formattedPhone}</a>;
                            })()
                          ) : '-'}
                        </span>
                      </div>
                      {escalation.intent_analysis?.product_mentioned && (
                        <div className="info-row">
                          <span className="info-label">{t('escalations.product_interest')}:</span>
                          <span className="info-value">{escalation.intent_analysis.product_mentioned}</span>
                        </div>
                      )}
                      {escalation.trigger_message && (
                        <div className="info-row">
                          <span className="info-label">{t('escalations.trigger_message')}:</span>
                          <span className="info-value trigger-message">"{escalation.trigger_message}"</span>
                        </div>
                      )}
                    </div>
                    <div className="escalation-meta">
                      <div className="meta-item">
                        <i className="bi bi-clock"></i>
                        <span>{formatTimeAgo(escalation.created_at)}</span>
                      </div>
                      {escalation.assigned_to && (
                        <div className="meta-item">
                          <i className="bi bi-person"></i>
                          <span>{escalation.assigned_to}</span>
                        </div>
                      )}
                      {escalation.confidence_score && (
                        <div className="meta-item">
                          <i className="bi bi-bar-chart"></i>
                          <span>{t('escalations.confidence')}: {(escalation.confidence_score * 100).toFixed(0)}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.total > pagination.limit && (
              <div className="pagination">
                <button
                  disabled={pagination.offset === 0}
                  onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                >
                  <i className="bi bi-chevron-left"></i> {t('common.previous')}
                </button>
                <span className="pagination-info">
                  {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} {t('common.of')} {pagination.total}
                </span>
                <button
                  disabled={pagination.offset + pagination.limit >= pagination.total}
                  onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                >
                  {t('common.next')} <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Escalation Detail Modal */}
      {selectedEscalation && (
        <div className="modal-overlay" onClick={() => setSelectedEscalation(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('escalations.detail.title')} #{selectedEscalation.id}</h2>
              <button className="modal-close" onClick={() => setSelectedEscalation(null)}>
                <i className="bi bi-x"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3>{t('escalations.detail.status_info')}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>{t('escalations.status.label')}</label>
                    <span className={`status-badge ${getStatusBadgeClass(selectedEscalation.status)}`}>
                      {t(`escalations.status.${selectedEscalation.status}`)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>{t('escalations.priority.label')}</label>
                    <span className={`priority-badge ${getPriorityBadgeClass(selectedEscalation.priority)}`}>
                      {t(`escalations.priority.${selectedEscalation.priority}`)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>{t('escalations.detail.type')}</label>
                    <span>{t(`escalations.type.${selectedEscalation.escalation_type}`)}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('escalations.detail.created_at')}</label>
                    <span>{formatDate(selectedEscalation.created_at)}</span>
                  </div>
                  {selectedEscalation.resolved_at && (
                    <div className="detail-item">
                      <label>{t('escalations.detail.resolved_at')}</label>
                      <span>{formatDate(selectedEscalation.resolved_at)}</span>
                    </div>
                  )}
                  {selectedEscalation.assigned_to && (
                    <div className="detail-item">
                      <label>{t('escalations.detail.assigned_to')}</label>
                      <span>{selectedEscalation.assigned_to}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="detail-section">
                <h3>{t('escalations.detail.contact_info')}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>{t('escalations.contact.name')}</label>
                    <span>{selectedEscalation.contact_name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('escalations.contact.email')}</label>
                    <span>
                      {selectedEscalation.contact_email ? (
                        <a href={`mailto:${selectedEscalation.contact_email}`}>{selectedEscalation.contact_email}</a>
                      ) : '-'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>{t('escalations.contact.phone')}</label>
                    <span>
                      {selectedEscalation.contact_phone ? (
                        (() => {
                          const formattedPhone = formatPhoneNumber(selectedEscalation.contact_phone);
                          return <a href={`tel:${formattedPhone}`}>{formattedPhone}</a>;
                        })()
                      ) : '-'}
                    </span>
                  </div>
                </div>
              </div>

              {selectedEscalation.trigger_message && (
                <div className="detail-section">
                  <h3>{t('escalations.detail.trigger_message')}</h3>
                  <div className="message-box">
                    {selectedEscalation.trigger_message}
                  </div>
                </div>
              )}

              {selectedEscalation.intent_analysis && (
                <div className="detail-section">
                  <h3>{t('escalations.detail.intent_analysis')}</h3>
                  <div className="detail-grid">
                    {selectedEscalation.intent_analysis.product_mentioned && (
                      <div className="detail-item">
                        <label>{t('escalations.product_interest')}</label>
                        <span>{selectedEscalation.intent_analysis.product_mentioned}</span>
                      </div>
                    )}
                    {selectedEscalation.confidence_score && (
                      <div className="detail-item">
                        <label>{t('escalations.confidence')}</label>
                        <span>{(selectedEscalation.confidence_score * 100).toFixed(0)}%</span>
                      </div>
                    )}
                    {selectedEscalation.intent_analysis.urgency && (
                      <div className="detail-item">
                        <label>{t('escalations.detail.urgency')}</label>
                        <span>{t(`escalations.urgency.${selectedEscalation.intent_analysis.urgency}`)}</span>
                      </div>
                    )}
                    {selectedEscalation.intent_analysis.reasoning && (
                      <div className="detail-item full-width">
                        <label>{t('escalations.detail.reasoning')}</label>
                        <span>{selectedEscalation.intent_analysis.reasoning}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedEscalation.resolution_notes && (
                <div className="detail-section">
                  <h3>{t('escalations.detail.resolution_notes')}</h3>
                  <div className="message-box">
                    {selectedEscalation.resolution_notes}
                  </div>
                </div>
              )}

              {selectedEscalation.status !== 'resolved' && selectedEscalation.status !== 'cancelled' && (
                <div className="detail-actions">
                  <button
                    className="btn-assign"
                    onClick={() => setAssignModal({ open: true, escalation: selectedEscalation, assignedTo: selectedEscalation.assigned_to || '' })}
                  >
                    <i className="bi bi-person-plus"></i> {t('escalations.actions.assign')}
                  </button>
                  <button
                    className="btn-resolve"
                    onClick={() => setResolveModal({ open: true, escalation: selectedEscalation })}
                  >
                    <i className="bi bi-check"></i> {t('escalations.actions.resolve')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Resolve Modal */}
      {resolveModal.open && (
        <div className="modal-overlay" onClick={() => setResolveModal({ open: false, escalation: null })}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('escalations.resolve.title')}</h2>
              <button className="modal-close" onClick={() => setResolveModal({ open: false, escalation: null })}>
                <i className="bi bi-x"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('escalations.resolve.notes_label')}</label>
                <textarea
                  value={resolveNotes}
                  onChange={(e) => setResolveNotes(e.target.value)}
                  placeholder={t('escalations.resolve.notes_placeholder')}
                  rows="5"
                />
              </div>
              <div className="modal-actions">
                <button className="btn-cancel" onClick={() => setResolveModal({ open: false, escalation: null })}>
                  {t('common.cancel')}
                </button>
                <button className="btn-primary" onClick={handleResolve}>
                  {t('escalations.resolve.submit')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {assignModal.open && (
        <div className="modal-overlay" onClick={() => setAssignModal({ open: false, escalation: null, assignedTo: '' })}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('escalations.assign.title')}</h2>
              <button className="modal-close" onClick={() => setAssignModal({ open: false, escalation: null, assignedTo: '' })}>
                <i className="bi bi-x"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('escalations.assign.assignee_label')}</label>
                <input
                  type="text"
                  value={assignModal.assignedTo}
                  onChange={(e) => setAssignModal(prev => ({ ...prev, assignedTo: e.target.value }))}
                  placeholder={t('escalations.assign.assignee_placeholder')}
                />
              </div>
              <div className="modal-actions">
                <button className="btn-cancel" onClick={() => setAssignModal({ open: false, escalation: null, assignedTo: '' })}>
                  {t('common.cancel')}
                </button>
                <button className="btn-primary" onClick={handleAssign}>
                  {t('escalations.assign.submit')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Escalations;

